import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { memberIdSchema } from '../identity/legacy-identity-id';

/** Which member-detail field a change record names. */
export const MEMBER_DETAIL_CHANGE_FIELD = [
  'call_phone',
  'secondary_email',
  'home_address',
  'shipping_address',
  'emergency_contact',
  'emergency_clinic',
  'parental_consent',
  'programme',
] as const;
export const memberDetailChangeFieldSchema = z.enum(MEMBER_DETAIL_CHANGE_FIELD);
export type MemberDetailChangeField = z.infer<typeof memberDetailChangeFieldSchema>;

/** Where the member's request came from (closed list). */
export const MEMBER_DETAIL_CHANGE_REQUEST_SOURCE = [
  'call',
  'care_chat',
  'email',
  'staff_correction',
] as const;
export const memberDetailChangeRequestSourceSchema = z.enum(MEMBER_DETAIL_CHANGE_REQUEST_SOURCE);
export type MemberDetailChangeRequestSource = z.infer<typeof memberDetailChangeRequestSourceSchema>;

export const MEMBER_CONTACT_CHANNEL_KIND = ['email', 'phone'] as const;
export const memberContactChannelKindSchema = z.enum(MEMBER_CONTACT_CHANNEL_KIND);
export type MemberContactChannelKind = z.infer<typeof memberContactChannelKindSchema>;

export const MEMBER_CONTACT_CHANNEL_SOURCE = [
  'registration',
  'profile_backfill',
  'staff_reconcile',
  'atlas_staff',
] as const;
export const memberContactChannelSourceSchema = z.enum(MEMBER_CONTACT_CHANNEL_SOURCE);
export type MemberContactChannelSource = z.infer<typeof memberContactChannelSourceSchema>;

/** Closed map from change field to the table both record ids belong to. */
export const MEMBER_DETAIL_CHANGE_RECORD_TABLE = {
  call_phone: 'member_contact_channels',
  secondary_email: 'member_contact_channels',
  home_address: 'member_addresses',
  shipping_address: 'member_addresses',
  emergency_contact: 'member_emergency_contacts',
  emergency_clinic: 'member_emergency_clinics',
  parental_consent: 'member_consents',
  programme: 'member_programs',
} as const satisfies Record<MemberDetailChangeField, string>;

export type MemberDetailChangeRecordTable =
  (typeof MEMBER_DETAIL_CHANGE_RECORD_TABLE)[MemberDetailChangeField];

const MEMBER_DETAIL_CHANGE_REASON_MAX = 500;

const identityRequiredSources = new Set<MemberDetailChangeRequestSource>([
  'call',
  'care_chat',
  'email',
]);

/**
 * The compliance block every member-detail edit carries (decision E). Parsed
 * once at the route boundary; services trust the typed value.
 */
export const memberDetailChangeSchema = z
  .object({
    requestSource: memberDetailChangeRequestSourceSchema,
    identityVerified: z.boolean().default(false),
    reason: z.string().trim().max(MEMBER_DETAIL_CHANGE_REASON_MAX).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (identityRequiredSources.has(value.requestSource)) {
      if (!value.identityVerified) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'tick_required',
          path: ['identityVerified'],
        });
      }
      return;
    }
    if (value.requestSource === 'staff_correction') {
      if (value.identityVerified) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'tick_forbidden',
          path: ['identityVerified'],
        });
      }
      if (!value.reason || !value.reason.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'reason_required',
          path: ['reason'],
        });
      }
    }
  });

export type MemberDetailChange = z.infer<typeof memberDetailChangeSchema>;

const memberDetailChangeActorSchema = z.object({
  internalUserId: z.string().uuid(),
  displayName: z.string(),
});

const memberDetailChangeDisplayValueSchema = z.object({
  display: z.string(),
});

export const memberDetailChangeItemSchema = z.object({
  id: z.string().uuid(),
  field: memberDetailChangeFieldSchema,
  requestSource: memberDetailChangeRequestSourceSchema,
  identityVerified: z.boolean(),
  reason: z.string().nullable(),
  createdAt: z.string().datetime({ offset: true }),
  actor: memberDetailChangeActorSchema,
  previous: memberDetailChangeDisplayValueSchema.nullable(),
  next: memberDetailChangeDisplayValueSchema,
});

export type MemberDetailChangeItem = z.infer<typeof memberDetailChangeItemSchema>;

export const memberDetailChangesDataSchema = z.object({
  items: z.array(memberDetailChangeItemSchema),
  /** True when the member has more rows than the server returns in one response. */
  listTruncated: z.boolean().optional(),
});

export const memberDetailChangesResponseSchema = apiEnvelope(memberDetailChangesDataSchema);
export type MemberDetailChangesResponse = z.infer<typeof memberDetailChangesResponseSchema>;

const memberDetailChangeReviewActorFacetSchema = z.object({
  internalUserId: z.string().uuid(),
  displayName: z.string(),
});

const memberDetailChangeReviewItemSchema = z.object({
  id: z.string().uuid(),
  memberId: memberIdSchema,
  memberRef: z.string(),
  field: memberDetailChangeFieldSchema,
  requestSource: memberDetailChangeRequestSourceSchema,
  identityVerified: z.boolean(),
  reason: z.string().nullable(),
  createdAt: z.string().datetime({ offset: true }),
  actor: memberDetailChangeActorSchema,
});

export const memberDetailChangeListDataSchema = z.object({
  items: z.array(memberDetailChangeReviewItemSchema),
  nextCursor: z.string().nullable(),
  facets: z.object({
    actors: z.array(memberDetailChangeReviewActorFacetSchema),
  }),
});

export const memberDetailChangeListResponseSchema = apiEnvelope(memberDetailChangeListDataSchema);
export type MemberDetailChangeListResponse = z.infer<typeof memberDetailChangeListResponseSchema>;

export const memberDetailChangeListQuerySchema = z
  .object({
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    actorInternalUserId: z.string().uuid().optional(),
    field: memberDetailChangeFieldSchema.optional(),
    cursor: z.string().min(1).optional(),
  })
  .strict();
